<template>
    <div class="c-file-input-wrapper" :class="{ invalid: error }">
        <div v-if="displayPreview && previewMode" class="preview-container mb-2">
            <label>{{ previewLabel }}</label>
            <slot name="preview" :files="files" :remove-file="removeFile" />

            <div v-if="!slots.preview" class="file-preview">
                <div v-for="(file, idx) of files" :key="`file-preview-${idx}`" class="file">
                    <c-preview :src="file" />
                    <div v-if="typeof file != 'string' || allowPreviewRemove" class="remove-overlay">
                        <c-button
                            v-tippy="{ placement: 'top', interactive: false }"
                            content="Delete"
                            size="sm"
                            icon="fa-solid fa-trash"
                            @click="removeFile(idx)"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="dragAndDrop"
            class="drag-container"
            :class="{
                'preview-mode': (displayPreview || slots.preview) && previewMode,
                disabled: disabled
            }"
            @dragover.prevent="handleDragOver"
            @drop.prevent="handleDrop"
        >
            <font-awesome-icon class="upload-icon" icon="fa-solid fa-cloud-arrow-up" />
            <span>
                Drag&amp;Drop or
                <c-button variant="link" text="Browse" class="p-0" :disabled="disabled" @click="openFileUploader" />
            </span>
        </div>
        <div v-else class="d-flex justify-content-between">
            <slot name="uploadTrigger" v-bind="{ onFileUpload: openFileUploader, disabled }" />
            <c-button
                v-if="!slots.uploadTrigger"
                :text="btnText"
                variant="outline-primary"
                :disabled="disabled"
                @click="openFileUploader"
            />
            <c-button
                v-if="previewMode"
                v-tippy="{ placement: 'top', interactive: false }"
                content="Delete"
                icon="fa-solid fa-trash"
                @click="removeFile()"
            />
        </div>
        <input
            ref="fileInputRef"
            class="d-none"
            type="file"
            :accept="accept"
            :multiple="multiple"
            :disabled="disabled"
            @change="handleFileChange"
        />
        <c-error v-if="error" :message="errorMsg" />
    </div>
</template>

<script setup lang="ts">
import { Ref, computed, onMounted, onUnmounted, ref, useSlots } from 'vue';
import CButton from '@/components/shared/CButton';
import CError from '@/components/shared/CError.vue';
import CPreview from '@/components/shared/CPreview.vue';
import { isValidFileSignature } from '@/components/shared/CFileInput/utils/fileSignatureValidation';
import { useToaster } from '@/composables/toaster';

const props = withDefaults(
    defineProps<{
        value: File[];
        initialFilesSrc?: string[];
        allowSrcRemove?: boolean;
        dragAndDrop?: boolean;
        multiple?: boolean;
        accept?: string;
        maxMbSize?: number;
        buttonText?: string;
        previewButtonText?: string;
        displayPreview?: boolean;
        previewLabel?: string;
        disabled?: boolean;
        error?: boolean;
        errorMsg?: string;
    }>(),
    { accept: '*', maxMbSize: 10, displayPreview: true, initialFilesSrc: () => [] }
);

const emit = defineEmits<{
    (event: 'input', value: File[]): void;
    (event: 'cancel'): void;
    (event: 'src-preview-removed', value: string[]): void;
}>();

const slots = useSlots();
const toaster = useToaster();

const fileInputRef: Ref<HTMLInputElement | null> = ref(null);

const _initialSrcs = ref<string[]>(props.initialFilesSrc);

const files = computed({
    get: () => (props.value.length ? props.value : [..._initialSrcs.value]),
    set: (files: string[] | File[]): void => emit('input', files as File[])
});

const previewMode = computed(() => files.value.length);

const allowPreviewRemove = computed(
    () => !props.disabled && ((files.value.length !== 1 && !props.dragAndDrop) || props.allowSrcRemove)
);

const btnText = computed(() => (previewMode.value ? props.previewButtonText : props.buttonText));

const openFileUploader = () => fileInputRef.value?.click();

const handleFileChange = async (event: Event) => {
    const element = event.target as HTMLInputElement;
    await updateModel(element.files);
};

const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;
    event.dataTransfer.dropEffect = 'copy';
};

const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    if (!event.dataTransfer || props.disabled) return;

    await updateModel(event.dataTransfer.files, props.multiple);
};

const removeFile = (index?: number) => {
    clearInput();

    if (index == undefined) {
        files.value = [];
        return;
    }

    if (typeof files.value[index] == 'string') {
        _initialSrcs.value.splice(index, 1);
        emit('src-preview-removed', _initialSrcs.value);
        return;
    }

    files.value.splice(index, 1);
};

const clearInput = () => {
    if (!fileInputRef.value) return;
    fileInputRef.value.value = '';
};

const updateModel = async (list: FileList | null, concat?: boolean) => {
    const newFiles: File[] = [];
    let invalidSignatureCount = 0;

    /**
     * Use a forof to manually push each file to model array,
     * since Array.from and spread operator dont work properly
     * clonning a FileList
     */
    for (const file of list || []) {
        if (!(await isValidFileSignature(file, props.accept))) {
            invalidSignatureCount += 1;
            continue;
        }
        if (!props.multiple && newFiles.length) break;
        newFiles.push(file);
    }
    if (invalidSignatureCount) {
        const message =
            invalidSignatureCount === 1
                ? 'This file is not valid. Please try another one or contact support.'
                : 'Some files are not valid. Please try others or contact support.';
        toaster.error(message);
    }

    files.value = concat ? [...(files.value as File[]), ...newFiles] : newFiles;
};

const selectionCanceled = () => {
    clearInput();
    emit('cancel');
};

onMounted(() => {
    fileInputRef.value?.addEventListener('cancel', selectionCanceled);
});

onUnmounted(() => {
    fileInputRef.value?.removeEventListener('cancel', selectionCanceled);
});

defineExpose({ openFileUploader });
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

.c-file-input-wrapper {
    &.invalid {
        .drag-container {
            border-color: get-color('red');
        }
    }
    .drag-container {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        font-size: 1rem;
        border-radius: 0.25rem;
        border: 2px dashed get-color('gray-200');
        width: 100%;
        padding: 1rem 3rem;
        font-weight: bold;
        .upload-icon {
            font-size: 3.5rem;
            color: get-color('gray-500');
            margin-bottom: 0.6rem;
        }
        .c-button-wrapper {
            line-height: unset;
        }

        &.disabled {
            color: get-color('gray-200');
        }

        &.preview-mode {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
            .upload-icon {
                display: none;
            }
        }
    }

    .preview-container {
        .file-preview {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            .file {
                position: relative;
                flex: 1;
                margin: 0 0.25rem;
                min-width: calc(33% - 0.25rem);
                max-width: calc(100% - 0.25rem);
                &:nth-child(n + 3) {
                    margin-top: 0.25rem;
                }
                .remove-overlay {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    border: 1px solid get-color('gray-200');
                    background-color: get-color('white');
                    border-radius: 0.25rem;
                    font-size: 1rem;
                }
            }
        }
    }
}
</style>
